import { sortAsc } from '@strata/helper-functions';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { FormContainer } from '@strata/react-form-container';
import { InlineMessage } from '@strata/react-inline-message';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import { isCapabilityActive } from 'services/CapabilityHelpers';
import { handleError } from 'services/ErrorManagement';
import { put, useGet, useGetWithRefetch } from 'services/Http';

export const LockManagementTab = ({ onSecondaryButtonClick = undefined }) => {
    const { npi } = useParams();

    const capabilities = useGet(`/reference/capabilities/${npi}`);
    const supplierChildrenResult = useGet(`/reference/suppliers/children/${npi}`);
    const segments = useGet('/reference/payer_segments');

    const [selectedSegmentId, setSelectedSegmentId] = useState();
    const pbmConfig = useGet(
        selectedSegmentId ? `/pbm/lock_configs/${selectedSegmentId}/display` : null,
    );

    const { result: lockManagementConfig, refetch } = useGetWithRefetch(
        selectedSegmentId ? `/reference/supplier_child_segments/${npi}/${selectedSegmentId}` : null,
    );

    /** Active + inactive children for lock config; merge segment API so saved inactive rows still appear. */
    const lockableSupplierLocations = useMemo(() => {
        const children = supplierChildrenResult?.children ?? [];
        const byNpi = new Map(children.map((child) => [child.supplier_npi, child]));
        (lockManagementConfig?.child_supplier_npis ?? []).forEach((seg) => {
            if (!byNpi.has(seg.child_supplier_npi)) {
                byNpi.set(seg.child_supplier_npi, {
                    supplier_npi: seg.child_supplier_npi,
                    organization_name: '(inactive location)',
                    ncpdp_id: seg.ncpdp_id ?? null,
                });
            }
        });
        return Array.from(byNpi.values()).sort((a, b) =>
            sortAsc(a.organization_name ?? '', b.organization_name ?? ''),
        );
    }, [supplierChildrenResult, lockManagementConfig]);

    const segmentOptions = [
        ...new Set(
            capabilities?.payer_capabilities
                ?.filter(isCapabilityActive)
                .map((cap) => cap.segment_id),
        ),
    ]
        .map((segmentId) => ({
            value: segmentId,
            secondaryData: segmentId,
            name:
                segments?.find((segment) => segment.segment_id === segmentId)?.segment_name ??
                segmentId,
        }))
        .sort((a, b) => sortAsc(a.name, b.name));

    return (
        <Formik
            initialValues={{
                segmentId: selectedSegmentId,
                pbmName: pbmConfig?.pbm_name ?? undefined,
                pbmNetworkCodeRequired: pbmConfig?.network_code_required ?? false,
                childNpis:
                    lockManagementConfig?.child_supplier_npis?.map(
                        (child) => child.child_supplier_npi,
                    ) ?? [],
                networkId: lockManagementConfig?.network_code ?? undefined,
            }}
            validationSchema={Yup.object({
                segmentId: Yup.string().required(validationStrings.requiredField.errorMessage),
                pbmName: Yup.string(),
                pbmNetworkCodeRequired: Yup.boolean().required(),
                childNpis: Yup.array(Yup.string().required())
                    .required(validationStrings.requiredField.errorMessage)
                    .min(1, validationStrings.requiredField.errorMessage),
                networkId: Yup.string().when(['pbmNetworkCodeRequired', 'childNpis'], {
                    is: (pbmNetworkCodeRequired, childNpis) =>
                        pbmNetworkCodeRequired && childNpis.length > 1,
                    then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
                }),
            })}
            enableReinitialize
            onSubmit={async (values, { resetForm }) => {
                try {
                    await put(`/reference/supplier_child_segments/${npi}/${selectedSegmentId}`, {
                        child_supplier_npis: values.childNpis,
                        network_code: values.networkId,
                    });
                    SuccessToast({ message: 'Lockable locations updated successfully.' });
                    await refetch();
                    resetForm();
                } catch (err) {
                    handleError('Error updating lockable locations', err);
                }
            }}
        >
            {(formik) => {
                /** If only one child is selected, that child must have an NCPDP ID */
                const missingNcpdpId =
                    formik.values.childNpis.length === 1 &&
                    !lockableSupplierLocations.find(
                        (child) => child.supplier_npi === formik.values.childNpis[0],
                    )?.ncpdp_id;
                return (
                    <FormContainer
                        formTitle="Manage Lockable Locations"
                        isLoading={!capabilities}
                        onFormSubmit={formik.handleSubmit}
                        isSubmitting={
                            formik.isSubmitting ||
                            !formik.isValid ||
                            !formik.dirty ||
                            missingNcpdpId
                        }
                        onSecondaryButtonClick={onSecondaryButtonClick ?? formik.resetForm}
                        formLeftContent={
                            <>
                                <SuggestSingleSelectFormik
                                    label="Payer Segment"
                                    id="segmentId"
                                    required
                                    values={segmentOptions}
                                    selectedItem={segmentOptions.find(
                                        (option) => option.value === formik.values.segmentId,
                                    )}
                                    onChange={(selectedItem) => {
                                        void (async () => {
                                            await formik.setFieldValue(
                                                'segmentId',
                                                selectedItem?.value,
                                            );
                                            setSelectedSegmentId(selectedItem?.value);
                                        })();
                                    }}
                                />
                                {formik.values.segmentId && (
                                    <>
                                        <TextInputFormik id="pbmName" label="PBM" disabled />
                                        <CheckboxListFormik
                                            name="childNpis"
                                            label="Supplier Locations"
                                            required
                                            showSelectAll
                                            options={lockableSupplierLocations.map((child) => ({
                                                value: child.supplier_npi,
                                                component: (
                                                    <>
                                                        {child.organization_name} (<b>NPI: </b>
                                                        {child.supplier_npi}, <b>NCPDP ID: </b>
                                                        {child.ncpdp_id ?? '--'})
                                                    </>
                                                ),
                                            }))}
                                        />
                                        {missingNcpdpId && (
                                            <InlineMessage
                                                intent={InlineMessage.Intent.ERROR}
                                                title="Missing NCPDP ID"
                                                label="If selecting only one location, it must have an NCPDP ID."
                                            />
                                        )}
                                        {formik.values.childNpis.length > 1 &&
                                            formik.values.pbmNetworkCodeRequired && (
                                                <div className="margin__bottom--medium">
                                                    <InlineMessage
                                                        intent={InlineMessage.Intent.INFO}
                                                        label={`${formik.values.pbmName} requires a Network ID if more than one location is selected.`}
                                                    />
                                                </div>
                                            )}
                                        <TextInputFormik
                                            id="networkId"
                                            label="Network ID"
                                            required={
                                                formik.values.childNpis.length > 1 &&
                                                formik.values.pbmNetworkCodeRequired
                                            }
                                        />
                                    </>
                                )}
                            </>
                        }
                    />
                );
            }}
        </Formik>
    );
};

LockManagementTab.propTypes = {
    onSecondaryButtonClick: PropTypes.func,
};
